import { useMemo, useState } from 'react'

import {
  Button,
  Checkbox,
  CheckboxGroup,
  IconButton,
  IconCaretDown,
  IconCaretUp,
  IconFaders,
  Search,
  Tooltip
} from '@vtex/shoreline'

import { useSidePanel } from '~/hooks/useSidePanel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import styles from '../../../components/advertiserBrand/styles.module.css'
import { useHotkey } from '../../../components/advertiserBrand/useHotkey'
import { usePushSearchParams } from '../../../components/advertiserBrand/usePushSearchParams'
import { useAdvertiserBrandOptions } from '../../../hooks/useAdvertiserBrands'

const FILTERS_PANEL_ID = 'advertiser-products-filters'

export type ProductsFiltersValue = {
  brands?: string[]
  unassigned?: boolean
}

/** "Sem marca" é uma opção da mesma lista: a API combina as duas por OR. */
const FiltersDrawerContent: React.FC<{
  value: ProductsFiltersValue
  onApply: (next: ProductsFiltersValue) => void
}> = ({ value, onApply }) => {
  const { t } = useAPPTranslation()
  const { options: brandOptions } = useAdvertiserBrandOptions()

  const [brands, setBrands] = useState<string[]>(value.brands ?? [])
  const [unassigned, setUnassigned] = useState(!!value.unassigned)
  const [brandQuery, setBrandQuery] = useState('')
  const [isOpen, setIsOpen] = useState(true)

  const toggleBrand = (name: string) =>
    setBrands(previous =>
      previous.includes(name)
        ? previous.filter(item => item !== name)
        : [...previous, name]
    )

  const visibleBrands = useMemo(() => {
    const needle = brandQuery.trim().toLowerCase()

    if (!needle) return brandOptions

    return brandOptions.filter(option =>
      option.label.toLowerCase().includes(needle)
    )
  }, [brandOptions, brandQuery])

  const activeCount = brands.length + (unassigned ? 1 : 0)

  return (
    <div className={styles.filterDrawer}>
      <div className={styles.filterBody}>
        <section className={styles.filterSection}>
          <button
            type="button"
            className={styles.filterSectionHeader}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(open => !open)}
          >
            <span className={styles.filterSectionTitle}>
              {t('rm:advertiserProducts.filters.brand')}
            </span>
            {!!activeCount && (
              <span className={styles.filterSectionBadge}>{activeCount}</span>
            )}
            {isOpen ? <IconCaretUp /> : <IconCaretDown />}
          </button>

          {isOpen && (
            <>
              <div className={styles.filterSearch}>
                <Search
                  value={brandQuery}
                  messages={{
                    placeholder: t('rm:advertiserBrands.search.placeholder')
                  }}
                  onChange={event => setBrandQuery(event.target.value)}
                  onClear={() => setBrandQuery('')}
                />
              </div>

              <CheckboxGroup
                label={
                  <span className="visually-hidden">
                    {t('rm:advertiserProducts.filters.brand')}
                  </span>
                }
                className={styles.filterGroup}
              >
                <div className={styles.filterOptions}>
                  <Checkbox
                    checked={unassigned}
                    onChange={() => setUnassigned(previous => !previous)}
                  >
                    {t('rm:advertiserProducts.noBrand')}
                  </Checkbox>

                  {visibleBrands.map(option => (
                    <Checkbox
                      key={option.value}
                      checked={brands.includes(option.value)}
                      onChange={() => toggleBrand(option.value)}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </div>
              </CheckboxGroup>
            </>
          )}
        </section>
      </div>

      <footer className={styles.filterFooter}>
        <Button
          variant="tertiary"
          disabled={!activeCount}
          onClick={() => {
            setBrands([])
            setUnassigned(false)
            onApply({})
          }}
        >
          {t('rm:advertiserProducts.filters.clear')}
        </Button>
        <Button
          variant="primary"
          onClick={() => onApply({ brands, unassigned })}
        >
          {t('rm:advertiserProducts.filters.apply')}
        </Button>
      </footer>
    </div>
  )
}

/** Gatilho do drawer, com contagem de filtros ativos e atalho `F`. */
export const ProductsFilters: React.FC<{ value: ProductsFiltersValue }> = ({
  value
}) => {
  const { t } = useAPPTranslation()
  const { addSidePanel, removeSidePanel } = useSidePanel()
  const pushParams = usePushSearchParams()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const activeCount = (value.brands?.length ?? 0) + (value.unassigned ? 1 : 0)

  const apply = (next: ProductsFiltersValue) => {
    pushParams(params => {
      params.delete('brand')
      next.brands?.forEach(name => params.append('brand', name))

      if (next.unassigned) params.set('unassigned', 'true')
      else params.delete('unassigned')

      params.set('page', '1')
    })
    setIsDrawerOpen(false)
    // só este painel: sem id, o hook fecha todos os que estiverem abertos
    removeSidePanel({ id: FILTERS_PANEL_ID })
  }

  const open = () => {
    if (isDrawerOpen) return

    setIsDrawerOpen(true)
    addSidePanel({
      id: FILTERS_PANEL_ID,
      title: t('rm:advertiserProducts.filters.label'),
      type: 'content',
      size: 'small',
      content: <FiltersDrawerContent value={value} onApply={apply} />,
      callbackRemove: () => setIsDrawerOpen(false)
    })
  }

  useHotkey(event => event.key.toLowerCase() === 'f', open)

  const label = `${t('rm:advertiserProducts.filters.label')} (F)`

  return (
    <span className={styles.filterTrigger}>
      <Tooltip label={label}>
        <IconButton variant="secondary" label={label} onClick={open}>
          <IconFaders />
        </IconButton>
      </Tooltip>
      {!!activeCount && (
        <span className={styles.filterBadge}>{activeCount}</span>
      )}
    </span>
  )
}
